import { useCallback, useEffect, useMemo, useState } from 'react';

import { CustomTooltip } from '@dynamika-shared/ui/Tooltip/CustomTooltip';
import { type MenuInfo } from 'rc-menu/lib/interface';
import { Checkbox } from '@dynamika-shared/ui/Checkbox/Checkbox';
import { DateRangeType } from '@dynamika-shared/ui/DatePicker/DateRangePicker';
import { makeAuditTrialsTableColumns } from './AuditTrialsList.column';
import { AvailableColumnsType } from './AuditTrialsList';
import { dateRange } from 'src/utils/DateTime';

import { AuditStateFilterItem, PossibleFilters, } from '@restore/auditTrailsFilters/auditFilters.types';
import { useAuditTrialsFiltersSlice } from '@restore/auditTrailsFilters/auditFilters.slice';

const initialColumns = {
  dateAndTime: true,
  actionBy: true,
  subjectId: true,
  visit: true,
  businessProcess: true,
  event: true,
  actionType: true,
  field: true,
  oldValue: true,
  newValue: true,
  reasonForModification: true,
};

const currentDate = new Date();
const [thirtyDaysAgoFormatted, nowFormatted] = dateRange(currentDate, 30);

export const useAuditTrialsFilters = () => {
  const [
    search,
    toggleActiveFilter,
    selectedFilters,
    toggleSelectedFilter,
    setSearch,
    setActionBySearch,
    state,
    allFilters,
    actionBySearch,
    reduxReset,
    eventSearch,
    setEventSearch,
  ] = useAuditTrialsFiltersSlice((state, actions) => [
    state.search,
    actions.toggleActiveFilter,
    state.selectedFilters,
    actions.toggleSelectedFilter,
    actions.setSearch,
    actions.setActionBySearch,
    state.state,
    state.allFilters,
    state.actionBySearch,
    actions.reset,
    state.eventSearch,
    actions.setEventSearch,
  ]);

  const availableSelectedFilters = useMemo(
    () => selectedFilters,
    [selectedFilters]
  );

  const [availableColumns, setAvailableColumns] =
    useState<AvailableColumnsType>(initialColumns);

  const [date, setDate] = useState<DateRangeType>([
    new Date(thirtyDaysAgoFormatted),
    new Date(nowFormatted),
  ]);

  const auditTableColumns = makeAuditTrialsTableColumns();

  const isoDateFormat = useMemo(() => date, [date]);

  useEffect(() => {
    return () => {
      reduxReset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDatePeriod = useCallback((calendarDate: DateRangeType) => {
    setDate(calendarDate);
  }, []);

  const filterOnClick = useCallback(
    (item: MenuInfo) => {
      toggleActiveFilter(item?.key as PossibleFilters);
    },
    [toggleActiveFilter]
  );

  const closeSubfilter = (name: PossibleFilters) => {
    toggleActiveFilter(name);
    if (actionBySearch?.length && setActionBySearch && name === 'actionBy') {
      setActionBySearch('');
    }
    if (search?.length && setSearch && name === 'subjectId') {
      setSearch('');
    }
    if (eventSearch?.length && setEventSearch && name === 'event') {
      setEventSearch('');
    }
  };

  const subFilters = useMemo(() => {
    return Object.entries(state) as [PossibleFilters, AuditStateFilterItem][];
  }, [state]);

  const filters = useMemo(() => {
    // TODO: unable this part when we will have more filters
    // const filteredColumns = searchValue
    //   ? filterInput(state, searchValue)
    //   : state;
    return [
      // TODO: unable this part when we will have more filters
      // {
      //   key: '2',
      //   type: 'group',
      //   label: (
      //     <div>
      //       <div className='mb-[8px] text-spanishGray'>Search</div>
      //       <Input
      //         value={searchValue}
      //         onChange={e => {
      //           setSearchValue(e.target.value);
      //         }}
      //         className='!mt-0 h-[4.8rem] !rounded-[3.2rem] px-2'
      //         placeholder='Search'
      //         suffix={
      //           <SearchSuffix
      //             disabled={!searchValue}
      //             action={() => {
      //               setSearchValue('');
      //             }}
      //           />
      //         }
      //       />
      //     </div>
      //   ),
      // },
      {
        key: '3',
        type: 'group' as const,
        label: (
          <div className='text-1216 text-spanishGray'>Set up your Filters</div>
        ),
        children: Object.entries(state)?.map(([key, filter]) => ({
          key: key,
          label: (
            <div className='flex justify-between'>
              <CustomTooltip label={filter.label} MAX_LABEL_LENGTH={18} />
              <Checkbox checked={state?.[key as PossibleFilters]?.active} />
            </div>
          ),
        })),
      },
    ];
    // }, [searchValue, state]);
  }, [state]);

  const args = useMemo(() => {
    return {
      dateAndTime: selectedFilters,
      actionByEmail: selectedFilters,
      actionByName: selectedFilters,
      actionBy: selectedFilters,
      subjectId: selectedFilters,
      visit: selectedFilters,
      businessProcess: selectedFilters,
      event: selectedFilters,
      actionType: selectedFilters,
      field: selectedFilters,
      newValue: selectedFilters,
      subjectCode: selectedFilters,
      reasonForModification: selectedFilters,
    };
  }, [selectedFilters]);

  // NOTE: they are used for display button/dropdown
  const displayFilters = useMemo(() => {
    return [
      {
        key: '1',
        type: 'group' as const,
        label: <div className='text-1216 text-spanishGray'>All Columns</div>,
        children: [
          {
            key: 'actionBy',
            label: (
              <div className='flex justify-between'>
                <CustomTooltip label={'Action by'} MAX_LABEL_LENGTH={18} />
                <Checkbox checked={availableColumns['actionBy']} />
              </div>
            ),
          },
          {
            key: 'dateAndTime',
            label: (
              <div className='flex justify-between'>
                <CustomTooltip label={'Date & Time'} MAX_LABEL_LENGTH={18} />
                <Checkbox checked={availableColumns['dateAndTime']} />
              </div>
            ),
          },
          {
            key: 'subjectId',
            label: (
              <div className='flex justify-between'>
                <CustomTooltip label={'Subject ID'} MAX_LABEL_LENGTH={18} />
                <Checkbox checked={availableColumns['subjectId']} />
              </div>
            ),
          },
          {
            key: 'visit',
            label: (
              <div className='flex justify-between'>
                <CustomTooltip label={'Visit'} MAX_LABEL_LENGTH={18} />
                <Checkbox checked={availableColumns['visit']} />
              </div>
            ),
          },
          {
            key: 'businessProcess',
            label: (
              <div className='flex justify-between'>
                <CustomTooltip
                  label={'Business Process'}
                  MAX_LABEL_LENGTH={18}
                />
                <Checkbox checked={availableColumns['businessProcess']} />
              </div>
            ),
          },
          {
            key: 'actionType',
            label: (
              <div className='flex justify-between'>
                <CustomTooltip label={'Action Type'} MAX_LABEL_LENGTH={18} />
                <Checkbox checked={availableColumns['actionType']} />
              </div>
            ),
          },
          {
            key: 'event',
            label: (
              <div className='flex justify-between'>
                <CustomTooltip label={'Event'} MAX_LABEL_LENGTH={18} />
                <Checkbox checked={availableColumns['event']} />
              </div>
            ),
          },
          {
            key: 'field',
            label: (
              <div className='flex justify-between'>
                <CustomTooltip label={'Field'} MAX_LABEL_LENGTH={18} />
                <Checkbox checked={availableColumns['field']} />
              </div>
            ),
          },
          {
            key: 'oldValue',
            label: (
              <div className='flex justify-between'>
                <CustomTooltip label={'Old Value'} MAX_LABEL_LENGTH={18} />
                <Checkbox checked={availableColumns['oldValue']} />
              </div>
            ),
          },
          {
            key: 'newValue',
            label: (
              <div className='flex justify-between'>
                <CustomTooltip label={'New Value'} MAX_LABEL_LENGTH={18} />
                <Checkbox checked={availableColumns['newValue']} />
              </div>
            ),
          },
          {
            key: 'reasonForModification',
            label: (
              <div className='flex justify-between'>
                <CustomTooltip
                  label={'Reason for Modification'}
                  MAX_LABEL_LENGTH={18}
                />
                <Checkbox checked={availableColumns['reasonForModification']} />
              </div>
            ),
          },
        ],
      },
      {
        key: '2',
        type: 'group' as const,
        label: (
          <div
            role='button'
            className='text-1216 text-center text-spanishGray text-[14px] hover:cursor-pointer'
            onClick={() => {
              setAvailableColumns(initialColumns);
            }}
          >
            Reset to default
          </div>
        ),
      },
    ];
  }, [availableColumns]);

  // Dynamic columns for audit trials table.
  const filteredTableColumns = useMemo(() => {
    return auditTableColumns.filter(column => {
      const key = column.name as keyof AvailableColumnsType;
      return availableColumns[key];
    });
  }, [availableColumns, auditTableColumns]);

  const selectColumns = useCallback(
    (e: keyof AvailableColumnsType) => {
      setAvailableColumns(prevState => {
        const key = e as keyof AvailableColumnsType;
        return {
          ...prevState,
          [e]: !prevState[key],
        };
      });
    },
    [setAvailableColumns]
  );

  return {
    displayFilters,
    filteredTableColumns,
    availableColumns,
    selectColumns,
    setDatePeriod,
    date,
    isoDateFormat,
    subFilters,
    filters,
    allFilters,
    toggleActiveFilter,
    toggleSelectedFilter,
    availableSelectedFilters,
    filterOnClick,
    args,
    closeSubfilter,
    setActionBySearch,
    actionBySearch,
    search,
    eventSearch,
    setEventSearch,
  };
};
